import { useCallback } from 'react';

import type { QueryStringKeyValue } from './types/query-string.key-value.type';
import type { QueryStringOptions } from './types/query-string.options';

/**
 * React hook for manipulating the query string in the browser's URL using React Router's `useSearchParams`.
 *
 * Provides methods to add, remove, and clear query string parameters.
 *
 * @param options - An object containing `searchParams` (the current URLSearchParams)
 * and `setSearchParams` (a function to update the search params).
 *
 * @returns An object with the following methods:
 * - `add`: Appends one or more key-value pairs to the query string.
 * - `clear`: Removes all query string parameters.
 * - `remove`: Deletes one or more keys from the query string.
 *
 * @example
 * ```tsx
 * const { add, clear, remove } = useQueryString();
 * add({ key: 'foo', value: 'bar' });
 * remove('foo');
 * clear();
 * ```
 */
export const useQueryString = ({ searchParams, setSearchParams }: QueryStringOptions) => {
  const add = useCallback(
    (...keyValuePair: QueryStringKeyValue[]) => {
      const next = new URLSearchParams(searchParams);

      for (const { key, value } of keyValuePair) {
        next.append(key, value);
      }

      setSearchParams(next);

      return next.toString();
    },
    [searchParams, setSearchParams]
  );

  const clear = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const remove = useCallback(
    (...keys: string[]) => {
      const next = new URLSearchParams(searchParams);

      for (const key of keys) {
        next.delete(key);
      }

      setSearchParams(next);

      return next.toString();
    },
    [searchParams, setSearchParams]
  );

  return {
    add,
    clear,
    remove,
  };
};
